import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { StudyMindMap } from './StudyMindMap';
import { getNoteMaps, saveStudyNote, type NoteWrite, type StudyNote } from './studyNotesApi';
const schema = z.object({ ids: z.array(z.string()).max(20, 'Link up to 20 maps.') });
export function StudyNoteMaps({
  note,
  owner,
  enabled,
  onPendingChange,
  onSaved,
  onOpenSource,
}: {
  note: StudyNote;
  owner: string;
  enabled: boolean;
  onPendingChange: (pending: boolean) => void;
  onSaved: (
    version: number,
    ids: string[],
    sources: NonNullable<StudyNote['linked_sources']>,
  ) => void;
  onOpenSource: (documentId: string) => void;
}) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['study-note-maps', owner, note.id, note.version],
    queryFn: () => getNoteMaps(note.id),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { ids: note.linked_artifact_ids ?? [] },
  });
  useEffect(() => {
    form.reset({ ids: note.linked_artifact_ids ?? [] });
  }, [note.version, note.linked_artifact_ids, form]);
  const [pending, setPending] = useState<NoteWrite | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const inFlight = useRef(false);
  const fresh = query.isFetchedAfterMount && !query.isError ? query.data : undefined;
  const maps = [...(fresh?.eligible ?? []), ...(fresh?.linked ?? [])].filter(
    (m, i, all) => all.findIndex((x) => x.id === m.id) === i,
  );
  async function submit(values: z.infer<typeof schema>) {
    if (inFlight.current || (!enabled && !pending) || !fresh) return;
    inFlight.current = true;
    const request: NoteWrite = pending ?? {
      id: note.id,
      request_id: crypto.randomUUID(),
      expected_version: note.version,
      title: note.title,
      body: note.body,
      topic: note.topic,
      subject_key: note.subject_key,
      term_id: note.term_id,
      class_date: note.class_date,
      pages: note.pages,
      status: note.status,
      linked_artifact_ids: values.ids,
    };
    setPending(request);
    onPendingChange(true);
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const result = await saveStudyNote(request);
      const ids = request.linked_artifact_ids ?? [];
      onSaved(
        result.version,
        ids,
        maps
          .filter((m) => ids.includes(m.id))
          .map((m) => ({ artifact_id: m.id, document_id: m.document_id })),
      );
      setPending(null);
      onPendingChange(false);
      setSaved(true);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['study-note', owner, note.id] }),
        client.invalidateQueries({ queryKey: ['study-notes', owner] }),
        client.invalidateQueries({ queryKey: ['study-note-maps', owner, note.id] }),
      ]);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Map links could not be saved.');
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500
      ) {
        setPending(null);
        onPendingChange(false);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <section
      className="mt-6 rounded-3xl border border-hairline bg-surface p-5"
      aria-label="Notebook mind maps"
    >
      <h2 className="text-lg font-extrabold text-ink">Mind maps from these pages</h2>
      <p className="mt-2 text-sm text-ink-soft">
        Link existing maps from your original photos. Linking uses no AI or Stars. Your own note
        text stays private. Deleting this note removes its links; the original maps remain in
        Materials.
      </p>
      {!enabled && !pending && (
        <p className="mt-2 text-sm">Save your note changes before linking maps.</p>
      )}
      {query.isError && (
        <p role="alert" className="mt-3">
          Maps could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => void query.refetch()}>
            Retry maps
          </button>
        </p>
      )}
      {!fresh && !query.isError && (
        <p className="mt-3" role="status">
          Loading saved maps…
        </p>
      )}
      {fresh && (
        <form onSubmit={form.handleSubmit(submit)} className="mt-4">
          {fresh.unavailable_links > 0 && (
            <p role="alert">
              {fresh.unavailable_links} linked maps are unavailable. Their content is hidden.
              Uncheck unavailable links to remove them.
            </p>
          )}
          <fieldset disabled={busy || Boolean(pending) || !enabled} className="space-y-3">
            {maps.map((m) => (
              <label
                key={m.id}
                className="flex min-h-11 items-center gap-3 rounded-xl bg-canvas-pure p-3 text-sm"
              >
                <input type="checkbox" value={m.id} {...form.register('ids')} />
                Page {m.page_number} · Map revision {m.revision} · {m.status}
              </label>
            ))}
            {(note.linked_artifact_ids ?? [])
              .filter((id) => !maps.some((m) => m.id === id))
              .map((id) => (
                <label key={id} className="flex min-h-11 items-center gap-3 text-sm">
                  <input type="checkbox" value={id} {...form.register('ids')} />
                  Unavailable map link
                </label>
              ))}
          </fieldset>
          {maps.length === 0 && !fresh.unavailable_links && (
            <p className="text-sm">
              No saved maps for these pages yet. File the original in Materials, then use Concepts
              in its subject to build a map and return here.
            </p>
          )}
          {fresh.truncated && (
            <p className="mt-3 text-sm">
              Showing the 50 most recent maps and all available linked maps.
            </p>
          )}
          {form.formState.errors.ids && <p role="alert">{form.formState.errors.ids.message}</p>}
          {error && (
            <p role="alert" className="mt-3">
              {error} {pending && 'The result is unconfirmed. Retry the same map links.'}
            </p>
          )}
          {(maps.length > 0 || Boolean(fresh.unavailable_links)) && (
            <button className="btn-pill-secondary mt-4" disabled={busy || (!enabled && !pending)}>
              {busy ? 'Saving map links…' : pending ? 'Retry map links' : 'Save map links'}
            </button>
          )}
        </form>
      )}
      {saved && (
        <p role="status" className="mt-3 font-bold">
          Map links saved.
        </p>
      )}
      {fresh?.linked.map((m) => (
        <article key={m.id} className="mt-5 min-w-0 rounded-2xl bg-canvas-pure p-4">
          <h3 className="font-bold">Page {m.page_number} · Linked mind map</h3>
          <p className="mt-2 text-sm">
            Revision {m.revision} ·{' '}
            {m.provenance === 'learner_authored'
              ? 'Your own map'
              : 'AI organised — check against the original'}{' '}
            · {m.status}
          </p>
          {m.status === 'outdated' && (
            <p className="mt-2 text-sm">The source changed. Check this map against the original.</p>
          )}
          <button
            type="button"
            className="btn-pill-ghost mt-2"
            onClick={() => onOpenSource(m.document_id)}
          >
            Open source page {m.page_number}
          </button>
          {m.map ? (
            <StudyMindMap map={m.map} />
          ) : (
            <p role="alert">
              This map revision cannot be displayed. Its original source is still available.
            </p>
          )}
        </article>
      ))}
    </section>
  );
}
